import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { reloadForUpdate } from '@/lib/swUpdate';

interface UpdateAvailableHintProps {
  serverVersion: string;
}

const CLOSE_DELAY_MS = 150;

export function UpdateAvailableHint({ serverVersion }: UpdateAvailableHintProps) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLSpanElement | null>(null);
  const openedByTap = useRef(false);
  const closeTimer = useRef<number | null>(null);
  const popoverId = useId();

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    if (closeTimer.current !== null) return;
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      openedByTap.current = false;
      closeTimer.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!open || openedByTap.current) return;
    const inside = (x: number, y: number) => {
      const w = wrapRef.current?.getBoundingClientRect();
      const p = popoverRef.current?.getBoundingClientRect();
      const hit = (r?: DOMRect) =>
        !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      return hit(w) || hit(p);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (inside(e.clientX, e.clientY)) cancelClose();
      else scheduleClose();
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, [open]);

  useEffect(() => {
    if (!open || !openedByTap.current) return;
    const onDoc = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        cancelClose();
        setOpen(false);
        openedByTap.current = false;
      }
    };
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [open]);

  const ariaLabel = t('update.ariaLabel');

  return (
    <span ref={wrapRef} className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-describedby={open ? popoverId : undefined}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'mouse') return;
          cancelClose();
          setOpen(true);
        }}
        onClick={() => {
          cancelClose();
          if (!isDesktop && !open) {
            setOpen(true);
            openedByTap.current = true;
            return;
          }
          reloadForUpdate();
        }}
        onFocus={() => {
          if (!isDesktop) return;
          cancelClose();
          setOpen(true);
        }}
        onBlur={() => {
          if (!openedByTap.current) scheduleClose();
        }}
        className="inline-flex items-center justify-center rounded-full text-slate-500 transition hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      >
        <RefreshCw
          className="h-3.5 w-3.5 animate-pulse"
          style={{ animationDuration: '3s' }}
        />
      </button>
      <span
        ref={popoverRef}
        role="dialog"
        aria-live="polite"
        id={popoverId}
        data-state={open ? 'open' : 'closed'}
        className="absolute left-1/2 top-full z-20 -translate-x-1/2 pt-2 transition-opacity duration-100 data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
      >
        <span className="block w-max max-w-[260px] whitespace-normal rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-xs leading-snug text-slate-800 shadow-lg">
          <Trans
            i18nKey="update.available"
            values={{ version: serverVersion }}
            components={{
              1: (
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    reloadForUpdate();
                  }}
                  className="cursor-pointer underline text-brand-600 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                />
              ),
            }}
          />
        </span>
      </span>
    </span>
  );
}
